import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Download, Clock, AlertCircle, Image, Camera, Share2, RefreshCw, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Placeholder image for loading states
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='600' viewBox='0 0 400 600'%3E%3Crect fill='%23f3f4f6' width='400' height='600'/%3E%3Ctext fill='%239ca3af' font-family='system-ui' font-size='16' x='50%25' y='50%25' text-anchor='middle'%3ELoading...%3C/text%3E%3C/svg%3E";

const PhotoDownload = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // Contact gate: downloads unlock after the customer leaves a WhatsApp
  // number (or taps the small DPDP-compliant skip link).
  const [gate, setGate] = useState(() => {
    try {
      if (localStorage.getItem(`piczoo_contact_${window.location.pathname.split('/').pop()}`)) return 'done';
      if (localStorage.getItem(`piczoo_contact_skip_${window.location.pathname.split('/').pop()}`)) return 'skipped';
    } catch (_) { /* private mode */ }
    return 'locked';
  });
  const unlocked = gate !== 'locked';
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await axios.get(`${API}/photo-sessions/${sessionId}`);
        setSession(response.data);
        
        // Calculate time remaining
        const expiresAt = new Date(response.data.expires_at);
        const now = new Date();
        const diffMs = expiresAt - now;
        if (diffMs > 0) {
          setTimeRemaining(Math.floor(diffMs / 1000));
        } else {
          setError('expired');
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('not_found');
        } else if (err.response?.status === 410) {
          setError('expired');
        } else {
          setError('unknown');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  // Update countdown timer
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setError('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Get the best image URL (S3 direct or API fallback)
  const getStripImageUrl = () => {
    if (session?.strip_url) {
      return session.strip_url; // Direct S3 URL
    }
    return `${API}/photo-sessions/${sessionId}/download`; // API fallback
  };

  // Get individual photo URL
  const getPhotoUrl = (photoNum) => {
    if (session?.photo_urls && session.photo_urls[photoNum - 1]) {
      return session.photo_urls[photoNum - 1]; // Direct S3 URL
    }
    return `${API}/photo-sessions/${sessionId}/photos/${photoNum}`; // API fallback
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Use API endpoint which will redirect to S3 - avoids CORS issues with direct S3 access
      const downloadUrl = `${API}/photo-sessions/${sessionId}/download`;
      
      const response = await axios.get(downloadUrl, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `piczoo-photos-${sessionId}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: try direct link download (works in some browsers)
      const directUrl = session?.strip_url || `${API}/photo-sessions/${sessionId}/download`;
      const link = document.createElement('a');
      link.href = directUrl;
      link.download = `piczoo-photos-${sessionId}.jpg`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Piczoo Photos',
          text: 'Check out my photos from the photobooth!',
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or share failed
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      } catch {
        toast.error('Could not copy link');
      }
    }
  };

  const formatTimeRemaining = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-50 to-rose-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-charcoal-900/20 border-t-charcoal-900 rounded-full animate-spin" />
          <p className="text-charcoal-800/60">Loading your photos...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-50 to-rose-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 shadow-xl max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-rose-100 rounded-full flex items-center justify-center">
            {error === 'expired' ? (
              <Clock className="w-10 h-10 text-rose-500" />
            ) : (
              <AlertCircle className="w-10 h-10 text-rose-500" />
            )}
          </div>
          
          <h1 className="font-serif text-2xl text-charcoal-900 mb-3">
            {error === 'expired' ? 'Photos Expired' : 'Photos Not Found'}
          </h1>
          
          <p className="text-charcoal-800/60 mb-6">
            {error === 'expired' 
              ? 'This photo session has expired. Photos are only available for 48 hours after printing.'
              : 'We couldn\'t find these photos. The link may be incorrect or the session has been removed.'}
          </p>

          <a
            href="/"
            className="inline-block px-8 py-3 bg-charcoal-900 text-white rounded-xl hover:bg-charcoal-800 transition-colors"
          >
            Visit Piczoo
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-white to-rose-50">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-lg mx-auto flex items-center justify-center">
          <img src="/piczoo-logo.png" alt="Piczoo" className="h-8" />
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto"
        >
          {/* Photo Preview Card */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6">
            {/* Strip Preview */}
            <div className="aspect-[2/3] bg-gradient-to-br from-charcoal-100 to-charcoal-200 relative">
              {/* Loading indicator */}
              {!imageLoaded && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-charcoal-400 animate-spin" />
                </div>
              )}
              
              <img
                src={getStripImageUrl()}
                alt="Your photo strip"
                className={`w-full h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  setImageError(true);
                  // Try fallback if S3 fails
                  if (session?.strip_url && e.target.src === session.strip_url) {
                    e.target.src = `${API}/photo-sessions/${sessionId}/download`;
                  } else {
                    e.target.style.display = 'none';
                  }
                }}
              />
              <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`}>
                <div className="text-center">
                  <Camera className="w-16 h-16 text-charcoal-400 mx-auto mb-2 opacity-20" />
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="p-6">
              <h1 className="font-serif text-2xl text-charcoal-900 mb-2 text-center">
                Your Photos Are Ready!
              </h1>
              
              <p className="text-charcoal-800/60 text-center mb-6">
                Download your photo strip to save the memories
              </p>

              {/* Time Remaining */}
              {timeRemaining !== null && timeRemaining > 0 && (
                <div className="flex items-center justify-center gap-2 text-sm text-charcoal-800/50 mb-6">
                  <Clock className="w-4 h-4" />
                  <span>Available for {formatTimeRemaining(timeRemaining)}</span>
                </div>
              )}

              {/* Contact gate — shown before download actions unlock */}
              {!unlocked && (
                <ContactCaptureCard sessionId={sessionId} gateMode onUnlock={setGate} />
              )}

              {/* Download Button */}
              {unlocked && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleDownload}
                disabled={downloading}
                className="w-full py-4 bg-charcoal-900 text-white rounded-2xl font-medium text-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-charcoal-800 transition-colors"
                data-testid="download-strip-btn"
              >
                {downloading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Download Photo Strip
                  </>
                )}
              </motion.button>
              )}

              {/* Share Section */}
              {unlocked && (
              <div className="mt-4 space-y-3" data-testid="share-section">
                <p className="text-xs text-charcoal-800/50 text-center uppercase tracking-wider">Share</p>
                <div className="grid grid-cols-4 gap-2">
                  {/* WhatsApp */}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Check out my photobooth pics! 📸 ${window.location.href}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 py-3 rounded-xl hover:bg-green-50 transition-colors"
                    data-testid="share-whatsapp"
                  >
                    <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    <span className="text-[10px] text-charcoal-800/60">WhatsApp</span>
                  </a>
                  
                  {/* Twitter/X */}
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`My photobooth moment! 📸`)}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 py-3 rounded-xl hover:bg-blue-50 transition-colors"
                    data-testid="share-twitter"
                  >
                    <svg className="w-6 h-6 text-charcoal-900" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    <span className="text-[10px] text-charcoal-800/60">X</span>
                  </a>
                  
                  {/* Instagram (copy link) */}
                  <button
                    onClick={handleShare}
                    className="flex flex-col items-center gap-1 py-3 rounded-xl hover:bg-purple-50 transition-colors"
                    data-testid="share-instagram"
                  >
                    <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    <span className="text-[10px] text-charcoal-800/60">Story</span>
                  </button>
                  
                  {/* Copy Link */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="flex flex-col items-center gap-1 py-3 rounded-xl hover:bg-charcoal-50 transition-colors"
                    data-testid="share-copy-link"
                  >
                    {copied ? (
                      <Check className="w-6 h-6 text-green-600" />
                    ) : (
                      <Copy className="w-6 h-6 text-charcoal-600" />
                    )}
                    <span className="text-[10px] text-charcoal-800/60">{copied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>

          {/* Individual Photos Section */}
          {unlocked && (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h2 className="font-medium text-charcoal-900 mb-4 flex items-center gap-2">
              <Image className="w-5 h-5" />
              Individual Photos
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((num) => (
                <a
                  key={num}
                  href={getPhotoUrl(num)}
                  download={`piczoo-photo-${num}.jpg`}
                  className="aspect-[3/2] bg-charcoal-100 rounded-xl overflow-hidden relative group hover:ring-2 ring-charcoal-900/20 transition-all"
                >
                  <img
                    src={getPhotoUrl(num)}
                    alt={`Photo ${num}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      // Try API fallback if S3 fails
                      const apiUrl = `${API}/photo-sessions/${sessionId}/photos/${num}`;
                      if (e.target.src !== apiUrl) {
                        e.target.src = apiUrl;
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-charcoal-900/0 group-hover:bg-charcoal-900/40 transition-colors flex items-center justify-center">
                    <Download className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </a>
              ))}
            </div>
          </div>
          )}

          {/* Contact capture — still offered below if the customer skipped */}
          {gate === 'skipped' && <ContactCaptureCard sessionId={sessionId} onUnlock={setGate} />}

          {/* Footer Note */}
          <p className="text-center text-sm text-charcoal-800/40 mt-6">
            Photos automatically expire 48 hours after printing · <a href="/privacy" className="underline hover:text-charcoal-800/70" data-testid="privacy-link">Privacy Policy</a>
          </p>
        </motion.div>
      </main>
    </div>
  );
};

const ContactCaptureCard = ({ sessionId, gateMode = false, onUnlock }) => {
  const storageKey = `piczoo_contact_${sessionId}`;
  const [done, setDone] = useState(() => !!localStorage.getItem(storageKey));
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const cleaned = phone.replace(/[\s-]/g, '');
    if (!/^(\+?91)?[6-9]\d{9}$/.test(cleaned)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!consent) { toast.error('Please tick the consent box to continue'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/customer-contacts`, {
        session_id: sessionId, name, email: null, phone: cleaned, consent,
      });
      localStorage.setItem(storageKey, '1');
      setDone(true);
      onUnlock?.('done');
      toast.success('Thanks! Your downloads are unlocked.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save — try again');
    }
    setSaving(false);
  };

  const skip = () => {
    try { localStorage.setItem(`piczoo_contact_skip_${sessionId}`, '1'); } catch (_) { /* noop */ }
    onUnlock?.('skipped');
  };

  if (done) {
    if (gateMode) return null;
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mt-6 text-center" data-testid="contact-capture-done">
        <p className="text-emerald-800 font-medium">You&apos;re on the list! We&apos;ll be in touch with offers and event invites.</p>
      </div>
    );
  }

  return (
    <div className={gateMode ? 'bg-cream-50 rounded-2xl border border-cream-300 p-5 mb-2' : 'bg-white rounded-2xl shadow-sm border border-cream-200 p-5 mt-6'} data-testid="contact-capture-card">
      <h3 className="font-serif text-lg text-charcoal-900 mb-1">
        {gateMode ? 'One quick step — get your photos on WhatsApp' : 'Loved your photos?'}
      </h3>
      <p className="text-sm text-charcoal-800/60 mb-4">
        {gateMode
          ? 'Drop your WhatsApp number for exclusive offers and event invites, then download away.'
          : 'Leave your WhatsApp number for offers & event invites.'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
          className="border border-cream-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-charcoal-900/20"
          data-testid="contact-name-input" maxLength={80}
        />
        <input
          value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp number" type="tel"
          className="border border-cream-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-charcoal-900/20"
          data-testid="contact-phone-input" maxLength={15}
        />
      </div>
      <label className="flex items-start gap-2 text-xs text-charcoal-800/60 mb-4 cursor-pointer">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="contact-consent-checkbox" />
        <span>
          I agree to receive offers and updates from Piczoo on WhatsApp, as described in the{' '}
          <a href="/privacy" target="_blank" rel="noreferrer" className="underline" data-testid="consent-privacy-link">Privacy Policy</a>.
          My details won&apos;t be shared with anyone else, and I can opt out anytime.
        </span>
      </label>
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={submit} disabled={saving}
          className="w-full sm:w-auto bg-charcoal-900 text-white text-sm px-6 py-2.5 rounded-xl hover:bg-charcoal-800 disabled:opacity-50"
          data-testid="contact-submit-btn"
        >
          {saving ? 'Saving…' : gateMode ? 'Unlock my photos' : 'Keep me posted'}
        </button>
        {gateMode && (
          <button
            onClick={skip}
            className="text-xs text-charcoal-800/40 hover:text-charcoal-800/70 underline"
            data-testid="contact-skip-link"
          >
            Skip, just download
          </button>
        )}
      </div>
    </div>
  );
};

export default PhotoDownload;
